import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import KpiCard from '../KpiCard.jsx';

/**
 * Can a buyer verify the supply path we send for each owner?
 *
 * WHY THIS TAB EXISTS
 * -------------------
 * `buildSupplyPathHealth` has run the correct check since 2026-09-16 -- it fetches the published
 * sellers.json and membership-tests every emitted sid, which is exactly what a buyer's SPO tooling
 * does. It has never had a reader. Open item #5 in claude/supply-chain-transparency-spo-status.md
 * says so in as many words: "No UI. The sweep is JSON on an admin endpoint."
 *
 * The cost of that was measured on 2026-09-30: the Admile seat went live emitting ADG636, which
 * was absent from biddigi.com/sellers.json, and stayed that way for six hours across 1,620 of the
 * day's 1,621 bids. The endpoint knew. Nothing showed it.
 *
 * THREE STATES, NOT TWO
 * ---------------------
 * `sellerIdPublished` is true / false / null, and null is NOT a failure. It means the published
 * file could not be read, so the membership test could not run. Rendering that as a red cross
 * would invent a problem; rendering it as a tick would invent an all-clear. It gets its own
 * "Unknown" treatment, and the file's own reachability is surfaced at the top as a finding in its
 * own right -- on 2026-09-16 the live file was invalid JSON for a period, during which every
 * seller in it was simultaneously unverifiable.
 */

function ownerName(o) {
  if (o.kind === 'publisher') return o.label || `Publisher #${o.publisherId}`;
  return o.label || `Seat #${o.inboundSeatId}`;
}

function publishedCell(o) {
  if (o.sellerIdPublished === true) return <span className="status-badge status-badge--active">Published</span>;
  if (o.sellerIdPublished === false) return <span className="status-badge status-badge--error">Not published</span>;
  return <span className="status-badge status-badge--neutral">Unknown</span>;
}

export default function SupplyPathTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showValid, setShowValid] = useState(false);

  function load() {
    setLoading(true);
    setError(null);
    api
      .supplyPathHealth()
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load supply path health.'))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const owners = data?.owners || [];
  const summary = data?.summary || {};
  const sj = data?.sellersJson;

  // Owners with no traffic (demo rows, publishers that have never had a seller_accounts row)
  // dominate the invalid count and are a backlog, not an incident. Live seats carry real bids, so
  // anything unverifiable there is costing money now and is listed separately and first.
  const liveSeats = owners.filter((o) => o.kind === 'inbound_seat' && o.status === 'active');
  const shown = showValid ? owners : owners.filter((o) => !o.valid);

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Supply path verifiability</h2>
          <button className="btn btn--ghost btn--sm" onClick={load} disabled={loading}>
            Re-run sweep
          </button>
        </div>
        <p className="card__hint">
          For each owner, the schain node BidDigi would send, and whether its seller id is actually
          present in the published sellers.json a buyer resolves it against. A buyer that cannot
          verify a path sends no error — it simply bids less, or stops.
        </p>

        {loading && <LoadingState label="Running sweep" />}
        {error && !loading && <EmptyState title="Couldn't load supply path health" message={error} />}

        {!loading && !error && data && (
          <>
            {sj && (
              <p className={`card__hint${sj.reachable ? '' : ' card__hint--warning'}`} style={{ marginTop: '10px' }}>
                <code className="code-chip">{sj.url}</code>{' '}
                {sj.reachable ? (
                  <>— reachable, {Number(sj.sellerCount).toLocaleString()} sellers listed.</>
                ) : (
                  <>
                    — <b>not readable</b>: {sj.error}. Every membership test below is therefore
                    Unknown, not failed.
                  </>
                )}
              </p>
            )}

            <div className="kpi-grid" style={{ marginTop: '14px' }}>
              <KpiCard label="Owners swept" value={summary.total ?? '—'} />
              <KpiCard label="Verifiable" value={summary.valid ?? '—'} />
              {/* The accent goes on the actionable number, not the reassuring one -- "12 not
                  verifiable" is what an operator opened this tab to find. */}
              <KpiCard label="Not verifiable" value={summary.invalid ?? '—'} tone="accent" />
              <KpiCard label="No seller account" value={summary.missingSellerAccount ?? '—'} />
            </div>
          </>
        )}
      </section>

      {!loading && !error && data && liveSeats.length > 0 && (
        <section className="card">
          <div className="card__header">
            <h2 className="card__title">Live inbound seats</h2>
          </div>
          <p className="card__hint">
            These carry real traffic. Anything not verifiable here is affecting bids now.
          </p>
          <div className="table-scroll" style={{ marginTop: '14px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Seat</th>
                  <th>Seat id</th>
                  <th>sid emitted</th>
                  <th>Published</th>
                  <th>Schain policy</th>
                </tr>
              </thead>
              <tbody>
                {liveSeats.map((o) => (
                  <tr key={`live-${o.inboundSeatId}`}>
                    <td className="cell-title">{ownerName(o)}</td>
                    <td className="cell-muted">{o.seatId || '—'}</td>
                    <td>{o.ownNode?.sid ? <code className="code-chip">{o.ownNode.sid}</code> : '—'}</td>
                    <td>{publishedCell(o)}</td>
                    <td>
                      {o.policyConfigured ? (
                        <span className="status-badge status-badge--active">Declared</span>
                      ) : (
                        <span className="status-badge status-badge--neutral">Inferred</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && !error && data && (
        <section className="card">
          <div className="card__header">
            <h2 className="card__title">All owners</h2>
            <label className="field field--checkbox field--checkbox-inline">
              <input type="checkbox" checked={showValid} onChange={(e) => setShowValid(e.target.checked)} />
              <span>Show verifiable owners too</span>
            </label>
          </div>

          {shown.length === 0 ? (
            <EmptyState title="Nothing to report" message="Every owner in this sweep is verifiable." />
          ) : (
            <div className="table-scroll" style={{ marginTop: '14px' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th>Kind</th>
                    <th>sid emitted</th>
                    <th>Published</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((o, i) => (
                    <tr key={`${o.kind}-${o.publisherId ?? o.inboundSeatId}-${i}`}>
                      <td className="cell-title">
                        {ownerName(o)}
                        {o.domain && <div className="cell-subtitle">{o.domain}</div>}
                      </td>
                      <td className="cell-muted">{o.kind === 'publisher' ? 'Publisher' : 'Inbound seat'}</td>
                      <td>{o.ownNode?.sid ? <code className="code-chip">{o.ownNode.sid}</code> : '—'}</td>
                      <td>{publishedCell(o)}</td>
                      <td className="cell-muted">{o.errors?.length ? o.errors[0] : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.asi?.note && (
            <p className="card__hint" style={{ marginTop: '14px' }}>
              {data.asi.note}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
